import React, { useEffect, useMemo, useRef, useState } from "react";
import { BiPlus } from "react-icons/bi";
import { HiOutlineAdjustmentsHorizontal } from "react-icons/hi2";
import {
  ALL_FORM_LIST_LIMIT,
  formTemplateStatusDefault,
  formTemplateStatusOptions,
  LOADER_STATUS,
  SORT_TYPE,
  USER_ROLE,
} from "Constants/constant";
import SelectOption from "components/common/Select/Select";
import TableHeader from "components/common/Table/TableHeader";
import Header from "components/common/Header/Header";
import { useNavigate } from "react-router";
import { roleAccess } from "helpers/RolesPermission";
import PaginationComponent from "components/common/Pagination/index";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import Search from "components/common/Search/Search";
// import { PERMISSION_KEYS, PERMISSION_KEYS_CHILDREN } from "store/constants";
import SilFormListActionButtons from "./SilFromsListActionsButtons";
import FormScopeBadge from "components/DynamicForms/shared/FormScopeBadge";
import {
  getFormScopeMeta,
  mapCategoryOption,
  orderFormCategoriesCustomAfterOther,
} from "components/DynamicForms/shared/formCategoryUtils";
import ManageFormCategoriesModal from "components/DynamicForms/shared/ManageFormCategoriesModal";

const SilFormsTable = ({
  templateList,
  isLoading,
  queryParams,
  formCategoriesList,
  handleFetch,
  getSortBy,
  paginationObj,
  setPaginationObj,
  routeRole = "sil",
  editFormTemplateHandler,
  isUserGracePeriodOver,
  fetchFormCategories,
}) => {
  const [search, setSearch] = useState(null);
  const [sortBy, setSortBy] = useState(queryParams.sort_field);
  const [selectedFormCategories, setSelectedFormCategories] = useState([]);
  const [showManageCategoriesModal, setShowManageCategoriesModal] = useState(false);
  const [manageCategoriesFocusCreate, setManageCategoriesFocusCreate] = useState(false);
  const [addedCategories, setAddedCategories] = useState([]);
  const [sortDescending, setSortDescending] = useState(
    queryParams.sort_order === SORT_TYPE.DESC ? true : false
  );
  const [renderLoading, setRenderLoading] = useState(LOADER_STATUS.NA);
  const [formTemplateStatus, setFormTemplateStatus] = useState(
    formTemplateStatusDefault
  );

  const [renderIsLoading, setRenderIsLoading] = useState(true);
  const initialFetchDoneRef = useRef(false);

  const navigate = useNavigate();
  const formScope = getFormScopeMeta(routeRole);
  const categoryOptions = useMemo(() => {
    const base = Array.isArray(formCategoriesList) ? formCategoriesList : [];
    const merged = [...base];
    addedCategories.forEach((category) => {
      if (!merged.some((item) => String(item.id) === String(category.id))) {
        merged.push(category);
      }
    });
    return orderFormCategoriesCustomAfterOther(
      merged.map((category) =>
        mapCategoryOption({
          ...category,
          is_custom: Boolean(category?.is_custom ?? category?.sp_id),
        })
      )
    );
  }, [formCategoriesList, addedCategories]);

  useEffect(() => {
    getSortBy(sortBy);
  }, [sortBy]);

  useEffect(() => {
    setSortBy(queryParams.sort_field ? queryParams.sort_field : null);
    if (queryParams.sort_order) {
      setSortDescending(
        queryParams.sort_order
          ? queryParams.sort_order === SORT_TYPE.DESC
            ? true
            : false
          : false
      );
    }
    if (queryParams.hasOwnProperty("filter") && queryParams.filter !== null) {
      const categories = queryParams.filter.form_category;
      setSelectedFormCategories(
        Array.isArray(categories) && categories.length > 0 ? categories : []
      );
    }
    if (queryParams.search) {
      setSearch(queryParams.search);
    }
  }, [queryParams]);

  useEffect(() => {
    setRenderLoading(LOADER_STATUS.PENDING);
    const isInitialFetch = !initialFetchDoneRef.current;
    const timeOutId = setTimeout(
      () => {
        if (!isLoading) {
          const payload = {
            status: formTemplateStatus.value,
            search: search,
            page: 1,
          };

          if (isInitialFetch) {
            payload.form_category = [];
          } else if (selectedFormCategories.length > 0) {
            payload.form_category = selectedFormCategories;
          }

          handleFetch(payload, sortBy);
          initialFetchDoneRef.current = true;
        }
      },
      isInitialFetch ? 0 : 1000
    );
    return () => clearTimeout(timeOutId);
  }, [search]);

  useEffect(() => {
    if (!isLoading && renderLoading === LOADER_STATUS.PENDING) {
      setRenderLoading(LOADER_STATUS.COMPLATE);
      setRenderIsLoading(false);
    }
  }, [isLoading]);

  const flipSort = (by) => {
    setSortBy(by);
    setSortDescending(!sortDescending);
    handleFetch({
      status: formTemplateStatus.value,
      sort_field: by,
      sort_order: !sortDescending ? SORT_TYPE.DESC : SORT_TYPE.ASC,
    });
  };

  return (
    <>
      <div className='form-admin-page bg-white'>
        <Header
          title='Forms'
          titleBadge={<FormScopeBadge scope={routeRole} />}
          desc={formScope.subtitle}
          {...(roleAccess([
            USER_ROLE.Super_Admin,
            USER_ROLE.Sp_Admin,
            USER_ROLE.Sp_manager,
          ]) === true && {
            // ) && //     : PERMISSION_KEYS_CHILDREN.CRM_FORM.CREATE //     ? PERMISSION_KEYS_CHILDREN.HRM_FORM.CREATE //   routeRole === "hrm" //     : PERMISSION_KEYS.CRM_FORM, //     ? PERMISSION_KEYS.HRM_FORM //   routeRole === "hrm" //   [], // roleAccess(
            handleClick: () => navigate("/sil/create-new-report"),
            btnLabel: `Create Home Forms`,
            icon: <BiPlus />,
            isButton: true,
          })}
        />

        <div className='form-admin-toolbar-wrap sm:px-6 lg:px-8 bg-white'>
          <div className='form-admin-toolbar'>
            <div className='form-admin-filter-field form-admin-filter-field--category'>
              <span className='form-admin-filter-label'>Category</span>
              <div className='form-admin-category-control'>
                <div className='form-admin-category-select'>
                  <SelectOption
                    cssClass='z-20'
                    placeHolder='All Categories'
                    isDisabled={isLoading || renderIsLoading}
                    isSearch={true}
                    selectedOption={selectedFormCategories}
                    handleChange={(e) => {
                      const nextSelection = Array.isArray(e) ? e : [];
                      handleFetch(
                        {
                          status: formTemplateStatus.value,
                          form_category: nextSelection,
                          page: 1,
                        },
                        sortBy
                      );
                      setSelectedFormCategories(
                        nextSelection.map((some) => ({
                          value: some.id,
                          label: some.label ?? some.name,
                          id: some.id,
                        }))
                      );
                    }}
                    data={categoryOptions}
                    isMulti={true}
                  />
                </div>
                {roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager]) && (
                  <div
                    className='form-admin-category-quick-actions'
                    role='group'
                    aria-label='Category management'
                  >
                    <button
                      type='button'
                      className='form-admin-category-icon-btn form-admin-category-icon-btn--primary'
                      onClick={() => {
                        setManageCategoriesFocusCreate(true);
                        setShowManageCategoriesModal(true);
                      }}
                      title='Create a new category'
                      aria-label='Create a new category'
                    >
                      <BiPlus aria-hidden='true' />
                    </button>
                    <button
                      type='button'
                      className='form-admin-category-icon-btn'
                      onClick={() => {
                        setManageCategoriesFocusCreate(false);
                        setShowManageCategoriesModal(true);
                      }}
                      title='Hide or show categories in this list'
                      aria-label='Manage category visibility'
                    >
                      <HiOutlineAdjustmentsHorizontal aria-hidden='true' />
                    </button>
                  </div>
                )}
              </div>
            </div>
            {roleAccess([
              USER_ROLE.Super_Admin,
              USER_ROLE.Sp_Admin,
              USER_ROLE.Sp_manager,
            ]) && (
              <div className='form-admin-filter-field form-admin-filter-field--status'>
                <span className='form-admin-filter-label'>Status</span>
                <SelectOption
                  isDisabled={isLoading || renderIsLoading}
                  isSearch={false}
                  selectedOption={formTemplateStatus}
                  handleChange={(e) => {
                    setFormTemplateStatus(e);
                    handleFetch({ status: e.value });
                  }}
                  data={formTemplateStatusOptions}
                  cssClass='z-20'
                />
              </div>
            )}
            <div className='form-admin-toolbar-search'>
              <span className='form-admin-filter-label'>Search</span>
              <Search
                setSearch={setSearch}
                search={search}
                width='w-full'
                placeholder='Search forms'
              />
            </div>
          </div>
        </div>
        <div className='flex flex-col sm:px-6 lg:px-8 pb-6 w-full'>
          <div className='-my-2 overflow-x-auto custom-scroll'>
            <div className='inline-block min-w-full py-2 align-middle'>
              <div className='form-admin-table-card'>
                {isLoading || renderIsLoading ? (
                  <div className='flex flex-col w-full min-h-[320px] form-page items-center justify-center'>
                    <CircularLoader
                      classes='flex justify-center items-center'
                      classloader='loader-l'
                    />
                  </div>
                ) : (
                  <table className='form-table min-w-full'>
                    <thead>
                      <tr>
                        <TableHeader
                          sortName='title'
                          flipSort={flipSort}
                          sortBy={sortBy}
                          sortDescending={sortDescending}
                          label='Title'
                          cssClass='px-3 py-3.5'
                        />
                        <TableHeader
                          isSort={false}
                          flipSort={flipSort}
                          sortBy={sortBy}
                          sortDescending={sortDescending}
                          label='Category'
                          cssClass='px-3 py-3.5'
                        />
                        <TableHeader
                          isSort={false}
                          flipSort={flipSort}
                          sortBy={sortBy}
                          sortDescending={sortDescending}
                          label='Subcategory'
                          cssClass='px-3 py-3.5'
                        />
                        {roleAccess([USER_ROLE.Super_Admin]) && (
                          <TableHeader
                            isSort={false}
                            flipSort={flipSort}
                            sortBy={sortBy}
                            sortDescending={sortDescending}
                            label='Form Status'
                            cssClass='px-3 py-3.5'
                          />
                        )}
                        <TableHeader
                          isSort={false}
                          flipSort={flipSort}
                          sortBy={sortBy}
                          sortDescending={sortDescending}
                          label='Action'
                          cssClass='form-table-actions-col px-3 py-3.5'
                          innerCssClass='justify-center'
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {templateList &&
                        templateList
                          .sort((a, b) => a.title.localeCompare(b.title))
                          .map((template) => (
                            <tr key={template.id}>
                              <td className='whitespace-nowrap px-3 text-sm'>
                                <div className='capitalize font-medium text-gray-900'>
                                  {template.title}
                                </div>
                              </td>
                              <td className='whitespace-nowrap px-3 text-sm text-gray-500'>
                                <div className='text-gray-900 capitalize'>
                                  {template.category_name}
                                </div>
                              </td>
                              <td className='whitespace-nowrap px-3 text-sm text-gray-500'>
                                {template.sub_category_name ? (
                                  <div className='text-gray-900 capitalize'>
                                    {template.sub_category_name}
                                  </div>
                                ) : (
                                  <span className='form-table-empty-cell'>—</span>
                                )}
                              </td>
                              {roleAccess([USER_ROLE.Super_Admin]) && (
                                <td className='whitespace-nowrap px-3 text-sm'>
                                  <span
                                    className={`form-status-badge ${
                                      queryParams?.filter?.status === "publish"
                                        ? "is-published"
                                        : "is-draft"
                                    }`}
                                  >
                                    {queryParams?.filter?.status === "publish"
                                      ? "Published"
                                      : "Draft"}
                                  </span>
                                </td>
                              )}
                              <td className='form-table-actions-col'>
                                <SilFormListActionButtons
                                  editFormTemplateHandler={
                                    editFormTemplateHandler
                                  }
                                  formTemplateStatus={formTemplateStatus}
                                  template={template}
                                  routeRole={routeRole}
                                  isUserGracePeriodOver={isUserGracePeriodOver}
                                  handleFetch={handleFetch}
                                  formCategoriesList={formCategoriesList}
                                />
                              </td>
                            </tr>
                          ))}
                      {renderLoading === LOADER_STATUS.COMPLATE &&
                        templateList?.length === 0 && (
                          <tr>
                            <td
                              colSpan={7}
                              className='py-6 text-center text-red-600'
                            >
                              No data found !
                            </td>
                          </tr>
                        )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {formCategoriesList && formCategoriesList.length > 0  &&
  templateList?.length > 0 &&
  paginationObj.total > 0 &&  (
        <div className='bg-white sm:px-6 lg:px-8 w-full pb-10'>
          <PaginationComponent
            page={paginationObj.page}
            activeListLength={templateList}
            limit={ALL_FORM_LIST_LIMIT}
            totalList={paginationObj.total}
            handlePagination={(e) => {
              const obj = paginationObj;
              obj.page = e;
              setPaginationObj({ ...obj });
              handleFetch(
                { page: e, status: formTemplateStatus.value },
                sortBy
              );
            }}
            totalPages={paginationObj.totalPages}
          />
        </div>
      )}
      <ManageFormCategoriesModal
        isOpen={showManageCategoriesModal}
        onClose={() => setShowManageCategoriesModal(false)}
        routeRole='sil'
        focusCreate={manageCategoriesFocusCreate}
        onUpdated={(created) => {
          if (created?.id) {
            setAddedCategories((prev) => [
              ...prev.filter((item) => String(item.id) !== String(created.id)),
              mapCategoryOption({ ...created, is_custom: true }),
            ]);
          } else {
            setAddedCategories([]);
          }
          fetchFormCategories?.();
          setSelectedFormCategories([]);
          handleFetch(
            {
              status: formTemplateStatus.value,
              form_category: [],
              page: 1,
            },
            sortBy
          );
          window.dispatchEvent(new CustomEvent("sil-form-categories-changed"));
        }}
      />
    </>
  );
};

export default SilFormsTable;
